import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, VStack, HStack, Input, Textarea, FormControl,
  Box, Text, Badge, useToast, Flex, IconButton, Select, Tooltip, Image,
  SimpleGrid, Divider, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Switch, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton } from '@chakra-ui/react';
import { FiPlus, FiCheck, FiX, FiRefreshCw, FiUpload, FiClipboard, FiFileText, FiMail, FiFolder, FiEdit2, FiCalendar, FiGrid, FiLink, FiEye, FiTrash2, FiCopy, FiGlobe, FiSlash, FiCheckSquare, FiPlay, FiActivity } from 'react-icons/fi';
import KioskFormBuilder from './KioskFormBuilder';
import WebFormsModal from './WebFormsModal';
import api from '../api';
import PageShell from './PageShell';
import CommunicationsModal from './CommunicationsModal';
import NotesModal from './NotesModal';
import ProspectsModal from './ProspectsModal';
import ConfirmModal from './ConfirmModal';
import InvoiceModal from './InvoiceModal';
import { AppointmentModal } from './BookMyDiaryDashboard';
import WorkSessionModal from './WorkSessionModal';
import ProspectEditModal from './ProspectEditModal';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'gray', bg: 'gray.50' },
  outreach_sent: { label: 'Outreach Sent', color: 'blue', bg: 'blue.50' },
  follow_up: { label: 'Follow Up', color: 'orange', bg: 'orange.50' },
  no_response: { label: 'No Response', color: 'red', bg: 'red.50' },
  engaged: { label: 'Engaged', color: 'green', bg: 'green.50' },
  rejection: { label: 'Rejection', color: 'red', bg: 'red.100' },
  slow_engagement: { label: 'Slow Engagement', color: 'yellow', bg: 'yellow.50' },
  conversion_likely_long: { label: 'Conversion - Long', color: 'purple', bg: 'purple.50' },
  conversion_likely_short: { label: 'Conversion - Short', color: 'purple', bg: 'purple.100' },
  buying_intent: { label: 'Buying Intent', color: 'green', bg: 'green.100' },
  converted: { label: 'Converted', color: 'green', bg: 'green.200' },
  lost: { label: 'Lost', color: 'gray', bg: 'gray.100' } };

const STATUS_GROUPS = [
  { title: 'Active Pipeline', statuses: ['buying_intent','conversion_likely_short','conversion_likely_long','engaged'] },
  { title: 'In Progress', statuses: ['follow_up','outreach_sent','new'] },
  { title: 'Needs Attention', statuses: ['no_response','slow_engagement'] },
  { title: 'Closed', statuses: ['converted','rejection','lost'] },
];

function EyebrowLabel({ children }) {
  return (
    <span style={{ display:'inline-block', background:'#2d3748', color:'white', padding:'2px 6px', borderRadius:'3px', marginBottom:'4px',
      fontSize:'8px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', lineHeight:1 }}>
      {children}
    </span>
  );
}

function CompanySearch({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const debounce = useRef(null);
  const search = async (val) => {
    if (val.length < 1) { setResults([]); return; }
    try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(val)}`); setResults(data); } catch(e) {}
  };
  return (
    <Box position="relative">
      <Input size="sm" value={q} placeholder="Search company..." onChange={(e) => {
        setQ(e.target.value); clearTimeout(debounce.current); debounce.current = setTimeout(() => search(e.target.value), 200);
      }} onBlur={() => setTimeout(() => setResults([]), 200)} />
      {results.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
          borderRadius="md" boxShadow="lg" zIndex={200} maxH="150px" overflowY="auto">
          {results.map(c => (
            <Box key={c.id} px={2} py={1} cursor="pointer" _hover={{ bg:'blue.50' }}
              onClick={() => { onSelect(c); setQ(c.name); setResults([]); }}>
              {c.name}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function ContactSearch({ companyId, onSelect }) {
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    if (companyId) { api.get(`/contacts?company_id=${companyId}`).then(r => setContacts(r.data)).catch(() => {}); }
  }, [companyId]);
  return (
    <Select size="sm" placeholder="Select contact..." onChange={(e) => {
      const ct = contacts.find(c => c.id === e.target.value);
      if (ct) onSelect(ct);
    }}>
      {contacts.map(ct => <option key={ct.id} value={ct.id}>{ct.first_name} {ct.last_name || ''}</option>)}
    </Select>
  );
}

export default function ProspectingDashboard({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, _setSelectedCampaign] = useState(null);

  // ── URL routing ───────────────────────────────────────────────────────
  // URL shape:
  //   /prospecting                                          → campaign list
  //   /prospecting/c/:campaignUuid                          → campaign detail
  //   /prospecting/c/:campaignUuid/kiosk-forms/new          → new kiosk form
  //   /prospecting/c/:campaignUuid/kiosk-forms/:formToken   → edit kiosk form
  const navigate = useNavigate();
  const location = useLocation();

  // Parse the current URL into its component parts. Memoised inside render.
  const urlParts = (() => {
    const m = location.pathname.match(/^\/prospecting\/c\/([^/]+)(?:\/kiosk-forms\/([^/]+))?/);
    if (!m) return { campaignUuid: null, kioskTokenOrNew: null };
    return { campaignUuid: m[1], kioskTokenOrNew: m[2] || null };
  })();

  // Wrapped setter: select a campaign AND push the URL.
  // If `c` is null, navigate back to the campaign list.
  const setSelectedCampaign = useCallback((c) => {
    _setSelectedCampaign(c);
    if (c && c.id) {
      navigate(`/prospecting/c/${c.id}`);
    } else if (location.pathname.startsWith('/prospecting/c/')) {
      navigate('/prospecting');
    }
  // eslint-disable-next-line
  }, [navigate]);

  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ statuses: {} });
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [campaignSort, setCampaignSort] = useState('created');
  const addProspectModalDisc = useDisclosure();
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editCampaignForm, setEditCampaignForm] = useState({});
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [existingMode, setExistingMode] = useState('company'); // 'company' or 'project'
  const [existingSearch, setExistingSearch] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [selectedExistingCompany, setSelectedExistingCompany] = useState(null);
  const [companyProjects, setCompanyProjects] = useState([]);
  const [selectedExistingProject, setSelectedExistingProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [projectNameResults, setProjectNameResults] = useState([]);
  const [addingExisting, setAddingExisting] = useState(false);
  const [projectLinkedCompanies, setProjectLinkedCompanies] = useState([]); // companies on the selected project
  const [pendingAddPayload, setPendingAddPayload] = useState(null); // { project, task } waiting for company pick
  const [campaignForm, setCampaignForm] = useState({ name: '', description: '', marketing_project_id: null });
  const [marketingProjects, setMarketingProjects] = useState([]);
  const [prospectForm, setProspectForm] = useState({ company: null, contact: null, notes: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Reuse exact same modals as MainGrid
  const commDisclosure = useDisclosure();
  const notesDisclosure = useDisclosure();
  const invoiceInDisclosure = useDisclosure();
  const invoiceOutDisclosure = useDisclosure();
  const invoiceQuoteDisclosure = useDisclosure();
  const diaryDisclosure = useDisclosure();
  const [modalProjectId, setModalProjectId] = useState(null);
  const [modalTaskId, setModalTaskId] = useState(null);
  const [diaryPrefill, setDiaryPrefill] = useState(null);
  const [contactPickerContacts, setContactPickerContacts] = useState([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactPickerEntry, setContactPickerEntry] = useState(null);
  const [pickerSelected, setPickerSelected] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerBookings, setPickerBookings] = useState([]);
  const [pickerBookingsLoading, setPickerBookingsLoading] = useState(false);
  const [rejectEntry, setRejectEntry] = useState(null);
  const [unrejectEntry, setUnrejectEntry] = useState(null);
  const [spamEntry, setSpamEntry]               = useState(null);
  const [spamConfirming, setSpamConfirming]     = useState(false);

  // Conversion log modal
  const [convLogEntry, setConvLogEntry]         = useState(null); // { id, oldVal, newVal }
  const [convLogNote, setConvLogNote]           = useState('');
  const [convLogSaving, setConvLogSaving]       = useState(false);
  // Pending slider value before confirmation
  const [pendingConv, setPendingConv]           = useState({});  // { [entryId]: sliderValue }

  // Work session & task edit
  const [workSessionEntry, setWorkSessionEntry] = useState(null);
  const workSessionDisclosure                   = useDisclosure();
  const [editTaskEntry, setEditTaskEntry]       = useState(null);
  const taskEditDisclosure                      = useDisclosure();
  const [historyEntry, setHistoryEntry]         = useState(null);
  const historyDisclosure                       = useDisclosure();

  // Sub-tasks drawer — lightweight inline list
  const [subTaskEntry, setSubTaskEntry]         = useState(null);
  const [subTasks, setSubTasks]                 = useState([]);
  const [subTasksLoading, setSubTasksLoading]   = useState(false);
  const [newSubTask, setNewSubTask]             = useState('');
  const subTaskDisclosure                       = useDisclosure();

  // Kiosk forms
  const [kioskForms, setKioskForms]             = useState([]);
  const [kioskLoading, setKioskLoading]         = useState(false);
  const [kioskBuilderOpen, _setKioskBuilderOpen] = useState(false);
  const [kioskEditForm, setKioskEditForm]       = useState(null);
  const [campaignTab, setCampaignTab]           = useState('prospects'); // 'prospects' | 'kiosk' | 'webforms'
  const [kioskDeleteId, setKioskDeleteId]       = useState(null);
  const toast = useToast();

  // Wrapped setter for the kiosk builder visibility — keeps the URL in sync.
  // Pushing the URL relies on selectedCampaign being set; if not, we just flip state.
  const setKioskBuilderOpen = useCallback((open) => {
    _setKioskBuilderOpen(open);
    if (open && selectedCampaign?.id) {
      // The current `kioskEditForm` (if any) tells us whether this is new vs edit.
      // We rely on the existing useState value at call time — call sites set
      // setKioskEditForm BEFORE setKioskBuilderOpen(true), so this read is
      // stale by one render. To work around that, the navigation actually
      // happens inside a useEffect below that watches kioskEditForm.
      const subPath = kioskEditForm?.token ? kioskEditForm.token : 'new';
      navigate(`/prospecting/c/${selectedCampaign.id}/kiosk-forms/${subPath}`);
    } else if (!open && selectedCampaign?.id && location.pathname.includes('/kiosk-forms/')) {
      navigate(`/prospecting/c/${selectedCampaign.id}`);
    }
  // eslint-disable-next-line
  }, [selectedCampaign, kioskEditForm, navigate]);

  // When the user switches from "new" to editing an existing form (or vice
  // versa) without closing the builder, keep the URL in sync.
  useEffect(() => {
    if (!kioskBuilderOpen || !selectedCampaign?.id) return;
    const subPath = kioskEditForm?.token ? kioskEditForm.token : 'new';
    const targetPath = `/prospecting/c/${selectedCampaign.id}/kiosk-forms/${subPath}`;
    if (location.pathname !== targetPath) navigate(targetPath, { replace: true });
  // eslint-disable-next-line
  }, [kioskBuilderOpen, kioskEditForm, selectedCampaign]);

  const openComms = (projectId, taskId) => { setModalProjectId(projectId); setModalTaskId(taskId || null); commDisclosure.onOpen(); };
  const openNotes = (projectId) => { setModalProjectId(projectId); notesDisclosure.onOpen(); };
  const openInvoiceIn = (projectId) => { setModalProjectId(projectId); invoiceInDisclosure.onOpen(); };
  const openInvoiceOut = (projectId) => { setModalProjectId(projectId); invoiceOutDisclosure.onOpen(); };
  const openInvoiceQuote = (projectId) => { setModalProjectId(projectId); invoiceQuoteDisclosure.onOpen(); };
  const openDiary = async (entry) => {
    // Fetch contacts for this prospect's company only
    let contacts = [];
    if (entry.company_id) {
      try { const { data } = await api.get(`/contacts?company_id=${entry.company_id}`); contacts = data || []; } catch(e) {}
    }
    // Fallback: if we have a direct contact_id on the entry, use that
    if (contacts.length === 0 && entry.contact_id) {
      contacts = [{ id: entry.contact_id, first_name: entry.first_name, last_name: entry.last_name, email: entry.contact_email }];
    }
    setContactPickerContacts(contacts);
    setContactPickerEntry(entry);
    setPickerSelected(contacts.length === 1 ? [contacts[0]] : []);
    setPickerSearch('');
    setPickerResults([]);
    setPickerBookings([]);
    setContactPickerOpen(true);
    // Load existing appointments for this company/project
    setPickerBookingsLoading(true);
    try {
      const params = entry.company_id ? `company_id=${entry.company_id}` : entry.project_id ? `project_id=${entry.project_id}` : null;
      if (params) {
        const { data } = await api.get(`/appointments?${params}`);
        setPickerBookings(data || []);
      }
    } catch(e) {}
    setPickerBookingsLoading(false);
  };

  const fetchCampaigns = useCallback(async () => {
    try { const { data } = await api.get('/prospects/campaigns'); setCampaigns(data); } catch(e) {}
  }, []);
  const fetchEntries = useCallback(async (cid) => {
    try {
      const [er, sr] = await Promise.all([api.get(`/prospects/entries?campaign_id=${cid}`), api.get(`/prospects/campaigns/${cid}/stats`)]);
      setEntries(er.data); setStats(sr.data);
    } catch(e) {}
  }, []);

  useEffect(() => {
    if (active) {
      fetchCampaigns();
      // If the user deep-linked to /prospecting/c/:id, leave that URL alone —
      // the URL→state reconciliation effect below will pick the campaign
      // once the campaigns list has loaded. Only clear when no deep link.
      if (!urlParts.campaignUuid) _setSelectedCampaign(null);
      fetchMarketingProjects();
    }
  // eslint-disable-next-line
  }, [isOpen, fetchCampaigns]);

  // ── URL → state reconciliation ──
  // Runs whenever the URL or the campaigns list changes. Handles:
  //   • Browser back/forward buttons
  //   • Deep linking via direct URL entry
  //   • External setSelectedCampaign(null) callers that did not navigate
  useEffect(() => {
    if (!active) return;
    const wantUuid = urlParts.campaignUuid;
    if (wantUuid) {
      // Need a campaign selected matching the URL UUID
      if (selectedCampaign?.id !== wantUuid) {
        const match = campaigns.find(c => c.id === wantUuid);
        if (match) {
          _setSelectedCampaign(match);
        } else if (campaigns.length === 0) {
          // Campaigns not yet loaded — wait for the next tick
          return;
        } else {
          // URL points to a non-existent campaign — fall back to list
          navigate('/prospecting', { replace: true });
        }
      }
      // Reconcile kiosk builder open state from URL
      if (urlParts.kioskTokenOrNew && !kioskBuilderOpen) {
        if (urlParts.kioskTokenOrNew === 'new') {
          setKioskEditForm(null);
          _setKioskBuilderOpen(true);
        } else if (kioskForms.length > 0) {
          const form = kioskForms.find(k => k.token === urlParts.kioskTokenOrNew);
          if (form) {
            setKioskEditForm(form);
            _setKioskBuilderOpen(true);
          }
        }
      } else if (!urlParts.kioskTokenOrNew && kioskBuilderOpen) {
        _setKioskBuilderOpen(false);
        setKioskEditForm(null);
      }
    } else {
      // URL is /prospecting (no campaign in path) → clear selection
      if (selectedCampaign) _setSelectedCampaign(null);
      if (kioskBuilderOpen) { _setKioskBuilderOpen(false); setKioskEditForm(null); }
    }
  // eslint-disable-next-line
  }, [active, location.pathname, campaigns, kioskForms]);

  const fetchMarketingProjects = async () => { try { const { data } = await api.get('/prospects/marketing-projects'); setMarketingProjects(data.all || []); } catch(e) {} };

  const fetchKioskForms = useCallback(async (campaignId) => {
    setKioskLoading(true);
    try { const { data } = await api.get(`/kiosk-forms?campaign_id=${campaignId}`); setKioskForms(data); }
    catch(e) { setKioskForms([]); }
    finally { setKioskLoading(false); }
  }, []);

  const handleSpam = async () => {
    if (!spamEntry) return;
    setSpamConfirming(true);
    try {
      await api.post(`/prospects/entries/${spamEntry.id}/spam`);
      setSpamEntry(null);
      if (selectedCampaign) fetchEntries(selectedCampaign.id);
    } catch(e) {}
    setSpamConfirming(false);
  };

  // Slider live preview (no API call yet)
  const handleConvLiveChange = (entryId, v) => {
    setPendingConv(p => ({ ...p, [entryId]: v }));
  };

  // Slider release — open confirmation modal
  const handleConvChangeEnd = (entry, v) => {
    const old = entry.conversion_likelihood || 0;
    if (v === old) return;
    setConvLogEntry({ id: entry.id, oldVal: old, newVal: v });
    setConvLogNote('');
  };

  // Save conversion change with note
  const saveConvLog = async () => {
    if (!convLogEntry) return;
    setConvLogSaving(true);
    try {
      await api.put(`/prospects/entries/${convLogEntry.id}`, {
        conversion_likelihood: convLogEntry.newVal,
        _conv_note: convLogNote || null,
      });
      // Update local entries state optimistically
      if (selectedCampaign) fetchEntries(selectedCampaign.id);
      setConvLogEntry(null);
      setPendingConv(p => { const n = {...p}; delete n[convLogEntry.id]; return n; });
    } catch(e) {}
    setConvLogSaving(false);
  };

  const cancelConvLog = () => {
    if (convLogEntry) {
      // Revert pending value
      setPendingConv(p => { const n = {...p}; delete n[convLogEntry.id]; return n; });
    }
    setConvLogEntry(null);
  };

  // Sub-tasks
  const openSubTasks = async (entry) => {
    setSubTaskEntry(entry);
    setSubTasksLoading(true);
    subTaskDisclosure.onOpen();
    try {
      const { data } = await api.get(`/sub-tasks?task_id=${entry.task_id || entry.project_id}`);
      setSubTasks(Array.isArray(data) ? data : []);
    } catch(e) { setSubTasks([]); }
    setSubTasksLoading(false);
  };

  const addSubTask = async () => {
    if (!newSubTask.trim() || !subTaskEntry) return;
    try {
      const taskId = subTaskEntry.task_id;
      if (!taskId) return;
      const { data } = await api.post('/sub-tasks', { task_id: taskId, title: newSubTask.trim() });
      setSubTasks(p => [...p, data]);
      setNewSubTask('');
    } catch(e) {}
  };

  const toggleSubTask = async (st) => {
    try {
      await api.put(`/sub-tasks/${st.id}`, { is_complete: !st.is_complete });
      setSubTasks(p => p.map(s => s.id === st.id ? { ...s, is_complete: !s.is_complete } : s));
    } catch(e) {}
  };

  const handleDeleteKioskForm = async (token) => {
    try {
      await api.delete(`/kiosk-forms/${token}`);
      if (selectedCampaign) fetchKioskForms(selectedCampaign.id);
      toast({ title: 'Form deleted', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: 'Delete failed', status: 'error', duration: 2000 }); }
    setKioskDeleteId(null);
  };
  useEffect(() => {
    if (selectedCampaign) {
      fetchEntries(selectedCampaign.id);
      fetchKioskForms(selectedCampaign.id);
      setCampaignTab('prospects');
    }
  }, [selectedCampaign, fetchEntries, fetchKioskForms]);

  const handleSaveCampaignEdit = async () => {
    if (!editCampaignForm.name) return; setSaving(true);
    try {
      await api.put(`/prospects/campaigns/${editingCampaign.id}`, editCampaignForm);
      await fetchCampaigns();
      setEditingCampaign(null);
    } catch(e) { toast({ title: 'Failed to save', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name) return; setSaving(true);
    try { const { data } = await api.post('/prospects/campaigns', campaignForm); await fetchCampaigns(); setShowNewCampaign(false); setCampaignForm({ name: '', description: '', marketing_project_id: null }); setSelectedCampaign(data); }
    catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); } finally { setSaving(false); }
  };
  const searchCompaniesForExisting = async (q) => {
    if (!q || q.length < 2) { setCompanySuggestions([]); return; }
    try {
      const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}`);
      setCompanySuggestions(Array.isArray(data) ? data : []);
    } catch(e) { setCompanySuggestions([]); }
  };

  const selectExistingCompany = async (co) => {
    setSelectedExistingCompany(co); setExistingSearch(co.name); setCompanySuggestions([]);
    setCompanyProjects([]); setSelectedExistingProject(null); setProjectTasks([]);
    const entryProjectIds = new Set(entries.map(e => e.project_id).filter(Boolean));
    try {
      // Use company NAME to bypass UUID issues - the backend resolves to INT directly
      const { data } = await api.get(`/projects/by-company-name?name=${encodeURIComponent(co.name)}`);
      const filtered = (data || []).filter(p =>
        !entryProjectIds.has(p.id)
      );
      setCompanyProjects(filtered);
    } catch(e) { setCompanyProjects([]); }
  };

  const selectExistingProject = async (proj) => {
    setSelectedExistingProject(proj); setProjectTasks([]); setProjectLinkedCompanies([]);
    try {
      const [tasksRes, companiesRes] = await Promise.all([
        api.get(`/tasks?project_id=${proj.id}`),
        api.get(`/projects/${proj.id}/companies`).catch(() => ({ data: [] })),
      ]);
      setProjectTasks(tasksRes.data || []);
      setProjectLinkedCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch(e) { setProjectTasks([]); }
  };

  const searchProjectsByName = async (q) => {
    const query = q !== undefined ? q : existingSearch;
    if (!query || query.length < 2) { setProjectNameResults([]); return; }
    const entryProjectIds = new Set(entries.map(e => e.project_id).filter(Boolean));
    try {
      // Use the dedicated search endpoint for proper LIKE matching
      const { data } = await api.get(`/projects/search?q=${encodeURIComponent(query)}&full=1&limit=30`);
      const results = Array.isArray(data) ? data : [];
      const filtered = results.filter(p =>
        p.project_type !== 'workflow_template' &&
        !entryProjectIds.has(p.id)
      );
      setProjectNameResults(filtered);
    } catch(e) { setProjectNameResults([]); }
  };

  const resetExistingSearch = () => {
    setExistingSearch(''); setCompanySuggestions([]); setSelectedExistingCompany(null);
    setCompanyProjects([]); setSelectedExistingProject(null); setProjectTasks([]);
    setProjectNameResults([]);
  };

  const handleAddExistingProject = async (project, task) => {
    if (!selectedCampaign) return;

    // Determine which company to use
    const companies = projectLinkedCompanies;
    let resolvedCompanyId = project.company_id || null;

    // If no primary company but linked companies exist, or multiple companies → show picker
    if (companies.length > 1 || (companies.length === 1 && !resolvedCompanyId)) {
      if (companies.length === 1) {
        // Only one linked company — use it automatically
        resolvedCompanyId = companies[0].company_id || companies[0].id;
      } else {
        // Multiple companies — ask user to pick
        setPendingAddPayload({ project, task });
        return;
      }
    } else if (!resolvedCompanyId && companies.length === 0) {
      // No companies at all — proceed without, show warning
      toast({ title: 'Warning: no company linked to this project', status: 'warning', duration: 3000 });
    }

    await doAddExistingProject(project, task, resolvedCompanyId);
  };

  const doAddExistingProject = async (project, task, companyId) => {
    setAddingExisting(true);
    try {
      await api.post('/prospects/entries', {
        campaign_id: selectedCampaign.id,
        project_id: project.id,
        company_id: companyId || null,
        notes: task ? `Task: ${task.task_title}` : null,
      });
      const label = task ? `"${task.task_title}" (${project.project_name})` : `"${project.project_name}"`;
      toast({ title: `${label} added to campaign`, status: 'success', duration: 2000 });
      await fetchEntries(selectedCampaign.id);
      setShowAddExisting(false); resetExistingSearch();
      setPendingAddPayload(null);
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Failed to add project', status: 'error', duration: 2000 });
    }
    setAddingExisting(false);
  };

  const handleAddProspect = async () => {
    if (!prospectForm.company) return; setSaving(true);
    try { await api.post('/prospects/entries', { campaign_id: selectedCampaign.id, company_id: prospectForm.company.id, contact_id: prospectForm.contact?.id || null, notes: prospectForm.notes || null });
      await fetchEntries(selectedCampaign.id); setShowAddProspect(false); setProspectForm({ company: null, contact: null, notes: '' }); }
    catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); } finally { setSaving(false); }
  };
  const handleStatusChange = async (id, s) => { try { await api.put(`/prospects/entries/${id}`, { prospect_status: s }); await fetchEntries(selectedCampaign.id); } catch(e) {} };
  const handleResponse = async (id, isR, reason) => { try { await api.post(`/prospects/entries/${id}/response`, { is_rejection: isR, rejection_reason: reason }); await fetchEntries(selectedCampaign.id); } catch(e) {} };
  const handleConversionChange = async (id, v) => { try { await api.put(`/prospects/entries/${id}`, { conversion_likelihood: v }); } catch(e) {} };
  const handleUnreject = async (id, note) => { try { await api.post(`/prospects/entries/${id}/unreject`, { note }); await fetchEntries(selectedCampaign.id); toast({ title: 'Prospect restored', status: 'success', duration: 2000 }); } catch(e) {} };

  // Parser (unchanged)
  const parseSpreadsheetData = (text) => {
    const rawLines = text.split('\n').filter(l => l.trim()); if (rawLines.length === 0) return [];
    const isTab = rawLines[0].includes('\t');
    const splitLine = (l) => isTab ? l.split('\t') : l.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
    const cleanCell = (c) => (c || '').replace(/^["'\s]+|["'\s]+$/g, '').trim();
    const HW = /company|name|email|phone|web|address|state|status|lead|source|contact|role|budget|require|notes|discussion|what|value|marketing|title|target|emailed/i;
    const scoreRow = (line) => { const cells = splitLine(line).map(cleanCell); let s = 0; cells.forEach(c => { if (HW.test(c)) s += 3; }); cells.forEach(c => { if (/^\d+$/.test(c)) s -= 1; }); return s + Math.min(cells.filter(c => c.length > 0).length, 10); };
    let bi = 0, bs = -999; for (let i = 0; i < Math.min(rawLines.length, 5); i++) { const s = scoreRow(rawLines[i]); if (s > bs) { bs = s; bi = i; } }
    const headers = splitLine(rawLines[bi]).map(c => cleanCell(c).toLowerCase());
    const cm = {}; const map = (k, ...pats) => { if (cm[k] !== undefined) return; for (const p of pats) { const i = headers.findIndex((h, j) => p.test(h) && !Object.values(cm).includes(j)); if (i >= 0) { cm[k] = i; return; } } };
    map('company', /^company$/, /company|business/); map('lead_from', /lead.?from/); map('emailed', /emailed/); map('state', /^state$/, /status|stage/, /target/);
    map('what_they_do', /what.*(they|do)/); map('requirement', /^require/); map('budget', /budget/); map('address', /^address/); map('website', /^web/);
    map('company_phone', /general.?phone/); map('contact_name', /^name$/, /contact.?name/); map('contact_email', /^email$/, /contact.?email/);
    map('contact_phone', /^phone$/, /contact.?phone/); map('contact_role', /^role$/); map('discussion_notes', /discussion/, /^notes$/); map('why_no', /why.?no/);
    if (cm.company === undefined) { let bc = -1, bv = 0; for (let c = 0; c < headers.length; c++) { if (Object.values(cm).includes(c)) continue; const vs = new Set(); for (let r = bi+1; r < Math.min(rawLines.length, bi+16); r++) { const v = cleanCell(splitLine(rawLines[r])[c]||''); if (v.length > 2 && !/^\d+$/.test(v)) vs.add(v); } if (vs.size > bv) { bv = vs.size; bc = c; } } if (bc >= 0) cm.company = bc; }
    const parsed = [];
    for (let i = bi + 1; i < rawLines.length; i++) { const cols = splitLine(rawLines[i]).map(cleanCell); const co = cols[cm.company]||''; if (!co || co.length < 2 || /^(to try|target|emailed|state|status)$/i.test(co)) continue;
      const g = (k) => (cm[k] !== undefined ? cols[cm[k]]||'' : ''); let cn = g('contact_name'); if (!cn && /^[A-Z][a-z]+\s+[A-Z]/.test(co)) cn = co;
      const ep = []; cols.forEach((c, j) => { if (!c || c.length < 2 || Object.values(cm).includes(j) || /^\d{1,3}$/.test(c)) return; ep.push(c); });
      parsed.push({ company: co, lead_from: g('lead_from'), emailed: g('emailed'), state: g('state'), what_they_do: g('what_they_do'), requirement: g('requirement'), budget: g('budget'), address: g('address'), website: g('website'), company_phone: g('company_phone'), contact_name: cn, contact_email: g('contact_email'), contact_phone: g('contact_phone'), contact_role: g('contact_role'), discussion_notes: g('discussion_notes'), why_no: g('why_no'), extra_notes: ep.join(' | ') }); }
    return parsed;
  };
  const handleParsePreview = () => { if (!importText.trim()) return; const p = parseSpreadsheetData(importText); setImportPreview(p); toast({ title: p.length > 0 ? `${p.length} prospects parsed` : 'No valid rows', status: p.length > 0 ? 'info' : 'warning', duration: 2000 }); };
  const handleFileUpload = (e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => { setImportText(ev.target.result); setImportPreview(parseSpreadsheetData(ev.target.result)); }; r.readAsText(f); e.target.value = ''; };
  const handleImport = async () => { if (importPreview.length === 0) return; setImporting(true); try { const { data } = await api.post(`/prospects/campaigns/${selectedCampaign.id}/import`, { rows: importPreview }); toast({ title: `Imported ${data.imported}, skipped ${data.skipped}${data.errors?.length ? `, ${data.errors.length} errors` : ''}`, status: data.imported > 0 ? 'success' : 'warning', duration: 4000 }); setShowImport(false); setImportText(''); setImportPreview([]); await fetchEntries(selectedCampaign.id); } catch(e) { toast({ title: 'Import failed', status: 'error', duration: 2000 }); } finally { setImporting(false); } };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '—';
  const fmtMoney = (v) => v === 0 ? null : '£' + Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const sumIn  = (arr) => arr.reduce((s, e) => s + parseFloat(e.inv_in_total  || 0), 0);
  const sumOut = (arr) => arr.reduce((s, e) => s + parseFloat(e.inv_out_total || 0), 0);
  const filteredEntries = statusFilter === 'all' ? entries
    : statusFilter === 'engaged_group' ? entries.filter(e => ['engaged','conversion_likely_long','conversion_likely_short','buying_intent'].includes(e.prospect_status))
    : entries.filter(e => e.prospect_status === statusFilter);
  const totalProspects = entries.length;
  const engagedCount = entries.filter(e => ['engaged','conversion_likely_long','conversion_likely_short','buying_intent'].includes(e.prospect_status)).length;
  const convertedCount = entries.filter(e => e.prospect_status === 'converted').length;
  const rejectedCount = entries.filter(e => e.prospect_status === 'rejection').length;

  // ═══════ CAMPAIGN LIST ═══════
  const CAMPAIGN_STATUS_COLORS = {
    active: { border: 'gray.300', top: 'gray.600', badge: 'purple', bg: 'white', hoverBg: 'gray.50', textColor: 'gray.800' },
    paused: { border: 'orange.300', top: 'orange.400', badge: 'orange', bg: 'orange.50', hoverBg: 'orange.100', textColor: 'gray.700' },
    completed: { border: 'green.300', top: 'green.500', badge: 'green', bg: 'white', hoverBg: 'green.50', textColor: 'gray.800' },
    retired: { border: 'gray.200', top: 'gray.300', badge: 'gray', bg: 'gray.50', hoverBg: 'gray.100', textColor: 'gray.400' },
    archived: { border: 'gray.200', top: 'gray.300', badge: 'gray', bg: 'gray.50', hoverBg: 'gray.100', textColor: 'gray.400' },
  };

  // When the kiosk form builder is open, it takes over the whole prospecting
  // surface as a full-page view (it uses its own PageShell internally).
  if (kioskBuilderOpen) {
    return (
      <KioskFormBuilder
        isOpen={kioskBuilderOpen}
        onClose={() => {
          setKioskBuilderOpen(false);
          setKioskEditForm(null);
          if (selectedCampaign) fetchKioskForms(selectedCampaign.id);
        }}
        campaignId={selectedCampaign?.id}
        campaignName={selectedCampaign?.name}
        editForm={kioskEditForm}
        onSaved={() => { if (selectedCampaign) fetchKioskForms(selectedCampaign.id); }}
      />
    );
  }

  if (!selectedCampaign) {
    const headerBtn = <Button size="sm" leftIcon={<FiPlus />} colorScheme="purple"
      onClick={() => setShowNewCampaign(!showNewCampaign)}>{showNewCampaign ? 'Cancel' : 'New Campaign'}</Button>;
    return (
      <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Prospecting Campaigns" headerRight={headerBtn}>

        {/* New Campaign Form */}
        {showNewCampaign && (
          <Box bg="purple.50" border="2px" borderColor="purple.300" borderRadius="lg" p={4} mb={5}>
            <Text fontWeight="700" color="purple.700" mb={3} fontSize="13px">New Campaign</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl><EyebrowLabel>Campaign Name *</EyebrowLabel>
                <Input size="sm" value={campaignForm.name} onChange={e => setCampaignForm(p => ({ ...p, name: e.target.value }))} />
              </FormControl>
              <FormControl><EyebrowLabel>Link to Marketing Campaign (optional)</EyebrowLabel>
                <Select size="sm" value={campaignForm.marketing_project_id || ''} placeholder="— None —"
                  onChange={e => setCampaignForm(p => ({ ...p, marketing_project_id: e.target.value || null }))}>
                  {marketingProjects.map(p => <option key={p.id} value={p.id}>{p.project_name}{p.row_group_name ? ` (${p.row_group_name})` : ''}</option>)}
                </Select>
              </FormControl>
              <FormControl gridColumn={{ md: 'span 2' }}><EyebrowLabel>Description</EyebrowLabel>
                <Textarea size="sm" rows={2} value={campaignForm.description} onChange={e => setCampaignForm(p => ({ ...p, description: e.target.value }))} />
              </FormControl>
            </SimpleGrid>
            <Flex justify="flex-end" mt={3}>
              <Button size="sm" bg="purple.600" color="white" _hover={{ bg:'purple.700' }} onClick={handleCreateCampaign} isLoading={saving}>Create Campaign</Button>
            </Flex>
          </Box>
        )}

        {/* Edit Campaign Form (full-width overlay above grid) */}
        {editingCampaign && (
          <Box bg="purple.50" border="2px" borderColor="purple.400" borderRadius="lg" p={4} mb={5}>
            <Text fontWeight="700" color="purple.700" mb={3} fontSize="13px">Edit: {editingCampaign.name}</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl isRequired><EyebrowLabel>Campaign Name</EyebrowLabel>
                <Input size="sm" value={editCampaignForm.name||''} onChange={e => setEditCampaignForm(p=>({...p,name:e.target.value}))} />
              </FormControl>
              <FormControl><EyebrowLabel>Status</EyebrowLabel>
                <Select size="sm" value={editCampaignForm.status||'active'} onChange={e => setEditCampaignForm(p=>({...p,status:e.target.value}))}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                  <option value="retired">Retired</option>
                  <option value="archived">Archived</option>
                </Select>
              </FormControl>
              <FormControl gridColumn={{ md: 'span 2' }}><EyebrowLabel>Description</EyebrowLabel>
                <Textarea size="sm" rows={2} value={editCampaignForm.description||''} onChange={e => setEditCampaignForm(p=>({...p,description:e.target.value}))} />
              </FormControl>
              <FormControl><EyebrowLabel>Marketing Campaign (optional)</EyebrowLabel>
                <Select size="sm" value={editCampaignForm.marketing_project_id||''} onChange={e => setEditCampaignForm(p=>({...p,marketing_project_id:e.target.value||null}))}>
                  <option value="">— None —</option>
                  {marketingProjects.map(p => <option key={p.id} value={p.id}>{p.project_name}{p.row_group_name?` (${p.row_group_name})`:''}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <HStack justify="space-between" h="100%" pt={4}>
                  <Box>
                    <EyebrowLabel>Include in Pipeline Dashboard</EyebrowLabel>
                    <Text fontSize="10px" color="gray.500">Show this campaign's invoices in Pipeline view</Text>
                  </Box>
                  <Switch size="sm" colorScheme="purple" isChecked={editCampaignForm.include_in_pipeline !== false}
                    onChange={e => setEditCampaignForm(p=>({...p,include_in_pipeline:e.target.checked}))} />
                </HStack>
              </FormControl>
            </SimpleGrid>
            <HStack justify="flex-end" spacing={2} mt={3}>
              <Button size="sm" variant="ghost" onClick={() => setEditingCampaign(null)}>Cancel</Button>
              <Button size="sm" bg="purple.600" color="white" _hover={{bg:'purple.700'}} onClick={handleSaveCampaignEdit} isLoading={saving}>Save Changes</Button>
            </HStack>
          </Box>
        )}

        {campaigns.length === 0 ? (
          <Text color="gray.500" textAlign="center" py={16}>No campaigns yet. Create one above.</Text>
        ) : (
          <>
            {/* Sort controls */}
            <HStack spacing={1} mb={4}>
              <Text fontSize="11px" color="gray.500" mr={1}>Sort:</Text>
              {[
                { key: 'created',   label: 'Date Created' },
                { key: 'prospects', label: 'Prospects' },
                { key: 'engaged',   label: 'Engaged' },
                { key: 'converted', label: 'Converted' },
              ].map(s => (
                <Button key={s.key} size="xs"
                  variant={campaignSort === s.key ? 'solid' : 'ghost'}
                  colorScheme={campaignSort === s.key ? 'purple' : 'gray'}
                  onClick={() => setCampaignSort(s.key)}>{s.label}</Button>
              ))}
            </HStack>

            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {[...campaigns].sort((a, b) => {
                if (campaignSort === 'prospects') return (b.prospect_count || 0) - (a.prospect_count || 0);
                if (campaignSort === 'engaged')   return (b.engaged_count   || 0) - (a.engaged_count   || 0);
                if (campaignSort === 'converted') return (b.converted_count || 0) - (a.converted_count || 0);
                return 0; // 'created' — preserve server order (DESC created_at)
              }).map(c => {
                const cs = CAMPAIGN_STATUS_COLORS[c.status || 'active'] || CAMPAIGN_STATUS_COLORS.active;
                const isEditing = editingCampaign?.id === c.id;
                const ci  = parseFloat(c.inv_in_total    || 0);
                const co  = parseFloat(c.inv_out_total   || 0);
                const cm  = parseFloat(c.marketing_cost  || 0);
                const cb  = ci - co;
                const net = cb - cm;
                const fmt = (v) => '£' + Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                const hasFinancials = ci > 0 || co > 0;
                return (
                  <Box key={c.id}
                    border="1px" borderColor={cs.border} borderRadius="lg"
                    borderTop="4px solid" borderTopColor={cs.top}
                    bg={cs.bg} opacity={isEditing ? 0.4 : 1}
                    boxShadow="sm" cursor="pointer"
                    transition="all 0.15s"
                    _hover={{ boxShadow: 'md', borderColor: cs.top }}
                    onClick={() => { if (!editingCampaign) setSelectedCampaign(c); }}
                    display="flex" flexDirection="column">

                    {/* Card header */}
                    <Flex px={3} pt={3} pb={1} justify="space-between" align="flex-start">
                      <Box flex={1} minW={0} pr={2}>
                        <Text fontWeight="700" fontSize="13px" color={cs.textColor} noOfLines={2} lineHeight="1.3">{c.name}</Text>
                        {c.status && c.status !== 'active' && (
                          <Badge colorScheme={cs.badge} fontSize="8px" textTransform="capitalize" mt={0.5}>{c.status}</Badge>
                        )}
                      </Box>
                      <IconButton icon={<FiEdit2 size={11}/>} size="xs" variant="ghost" colorScheme="purple"
                        aria-label="Edit campaign" flexShrink={0}
                        onClick={e => { e.stopPropagation(); setEditingCampaign(c); setEditCampaignForm({name:c.name, description:c.description||'', status:c.status||'active', include_in_pipeline: c.include_in_pipeline !== false, marketing_project_id: c.marketing_project_id||''}); }} />
                    </Flex>

                    {/* Description */}
                    {c.description && (
                      <Text px={3} pb={2} fontSize="11px" color="gray.500" noOfLines={2} lineHeight="1.4">{c.description}</Text>
                    )}

                    {/* Spacer */}
                    <Box flex={1} />

                    {/* Stats footer */}
                    <Box px={3} py={2} borderTop="1px" borderColor="gray.100" mt={2}>
                      <Flex justify="space-between" align="flex-end">
                        {/* Left: badges */}
                        <Box>
                          <Flex gap={1} flexWrap="wrap" mb={hasFinancials ? 1 : 0}>
                            <Badge colorScheme="purple" fontSize="9px" px={1.5}>{c.prospect_count || 0} prospects</Badge>
                            {c.engaged_count > 0 && <Badge colorScheme="green" fontSize="9px" px={1.5}>{c.engaged_count} engaged</Badge>}
                            {c.converted_count > 0 && <Badge colorScheme="teal" fontSize="9px" px={1.5}>{c.converted_count} converted</Badge>}
                            {c.include_in_pipeline === false && <Badge colorScheme="gray" fontSize="9px" variant="outline" px={1.5}>Excl. Pipeline</Badge>}
                          </Flex>
                        </Box>

                        {/* Right: stacked financials */}
                        {hasFinancials && (
                          <Box textAlign="center" flexShrink={0} ml={2}>
                            {ci > 0 && (
                              <Text fontSize="10px" fontWeight="700" color="green.600" lineHeight="1.5">IN {fmt(ci)}</Text>
                            )}
                            {co > 0 && (
                              <Text fontSize="10px" fontWeight="700" color="red.500" lineHeight="1.5">OUT {fmt(co)}</Text>
                            )}
                            <Text fontSize="10px" fontWeight="800" color={cb >= 0 ? 'green.700' : 'red.600'} lineHeight="1.5"
                              borderTop="1px" borderColor="gray.200" mt={0.5} pt={0.5}>
                              BAL {cb < 0 ? '-' : ''}{fmt(cb)}
                            </Text>
                            {cm > 0 && (
                              <>
                                <Text fontSize="10px" fontWeight="700" color="orange.500" lineHeight="1.5" mt={1}>MKT {fmt(cm)}</Text>
                                <Text fontSize="10px" fontWeight="800" color={net >= 0 ? 'green.700' : 'red.600'} lineHeight="1.5"
                                  borderTop="1px" borderColor="gray.200" mt={0.5} pt={0.5}>
                                  NET {net < 0 ? '-' : ''}{fmt(net)}
                                </Text>
                              </>
                            )}
                          </Box>
                        )}
                      </Flex>
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </PageShell>
    );
  }

  // ═══════ CAMPAIGN DETAIL ═══════
  const detailHeaderRight = (
    <HStack spacing={1}>
      <IconButton icon={<FiRefreshCw />} size="xs" variant="ghost" onClick={() => fetchEntries(selectedCampaign.id)} aria-label="Refresh" />
      <Button size="xs" leftIcon={<FiGlobe />} colorScheme={campaignTab === 'webforms' ? 'blue' : 'gray'} variant={campaignTab === 'webforms' ? 'solid' : 'outline'}
        onClick={() => setCampaignTab(t => t === 'webforms' ? 'prospects' : 'webforms')}>
        Web Forms
      </Button>
      <Button size="xs" leftIcon={<FiGrid />} colorScheme={campaignTab === 'kiosk' ? 'orange' : 'gray'} variant={campaignTab === 'kiosk' ? 'solid' : 'outline'}
        onClick={() => setCampaignTab(t => t === 'kiosk' ? 'prospects' : 'kiosk')}>
        Kiosk Forms {kioskForms.length > 0 && `(${kioskForms.length})`}
      </Button>
      {campaignTab === 'prospects' && <>
        <Button size="xs" leftIcon={<FiUpload />} colorScheme="teal" variant="outline" onClick={() => { setShowImport(!showImport); setShowAddProspect(false); setShowAddExisting(false); }}>{showImport ? 'Cancel' : 'Import'}</Button>
        <Button size="xs" leftIcon={<FiFolder />} colorScheme="blue" variant="outline" onClick={() => { setShowAddExisting(!showAddExisting); setShowAddProspect(false); setShowImport(false); setExistingSearch(''); setProjectNameResults([]); setCompanySuggestions([]); setSelectedExistingCompany(null); setCompanyProjects([]); setSelectedExistingProject(null); }}>{showAddExisting ? 'Cancel' : 'Add Existing Project'}</Button>
        <Button size="xs" leftIcon={<FiPlus />} colorScheme="purple" variant="outline" onClick={() => { setShowImport(false); setShowAddExisting(false); setShowAddProspect(false); setProspectForm({company:null,contact:null,notes:''}); addProspectModalDisc.onOpen(); }}>Add Prospect</Button>
      </>}
      {campaignTab === 'kiosk' && (
        <Button size="xs" leftIcon={<FiPlus />} colorScheme="orange" onClick={() => { setKioskEditForm(null); setKioskBuilderOpen(true); }}>New Form</Button>
      )}
    </HStack>
  );
  const detailBack = fullPage ? (() => { setSelectedCampaign(null); fetchCampaigns(); }) : undefined;

  return (
    <>
    <PageShell fullPage={fullPage} onBack={detailBack || onBack} isOpen={isOpen} onClose={onClose}
      title={`Prospecting Campaign : ${selectedCampaign.name}`} headerRight={detailHeaderRight}>
          {/* Stats */}
          {(() => {
            const engagedEntries   = entries.filter(e => ['engaged','conversion_likely_long','conversion_likely_short','buying_intent'].includes(e.prospect_status));
            const convertedEntries = entries.filter(e => e.prospect_status === 'converted');
            const rejectedEntries  = entries.filter(e => e.prospect_status === 'rejection');
            const FinStrip = ({ arr, accentIn='green.700', accentOut='red.600' }) => {
              const i = sumIn(arr), o = sumOut(arr), bal = i - o;
              if (i === 0 && o === 0) return null;
              return (
                <Box mt={1} pt={1} borderTop="1px" borderColor="gray.200">
                  {i > 0 && <Flex justify="space-between"><Text fontSize="9px" color="gray.400">IN</Text><Text fontSize="9px" fontWeight="700" color={accentIn}>{fmtMoney(i)}</Text></Flex>}
                  {o > 0 && <Flex justify="space-between"><Text fontSize="9px" color="gray.400">OUT</Text><Text fontSize="9px" fontWeight="700" color={accentOut}>{fmtMoney(o)}</Text></Flex>}
                  {(i > 0 || o > 0) && <Flex justify="space-between" mt={0.5}><Text fontSize="9px" color="gray.400" fontWeight="600">BAL</Text><Text fontSize="9px" fontWeight="800" color={bal >= 0 ? 'green.700' : 'red.600'}>{bal >= 0 ? '' : '-'}{fmtMoney(Math.abs(bal))}</Text></Flex>}
                </Box>
              );
            };
            return (
              <SimpleGrid columns={5} spacing={3} mb={3}>
                <Box bg="purple.50" p={2} borderRadius="md" cursor="pointer"
                  onClick={() => setStatusFilter('all')} border={statusFilter === 'all' ? '2px solid' : 'none'} borderColor="purple.400">
                  <Text fontSize="20px" fontWeight="800" color="purple.600" textAlign="center">{totalProspects}</Text>
                  <Text fontSize="10px" color="gray.500" textAlign="center">Prospects</Text>
                  <FinStrip arr={entries} />
                </Box>
                <Box bg="green.50" p={2} borderRadius="md" cursor="pointer"
                  onClick={() => setStatusFilter(statusFilter === 'engaged_group' ? 'all' : 'engaged_group')} border={statusFilter === 'engaged_group' ? '2px solid' : 'none'} borderColor="green.400">
                  <Text fontSize="20px" fontWeight="800" color="green.600" textAlign="center">{engagedCount}</Text>
                  <Text fontSize="10px" color="gray.500" textAlign="center">Engaged</Text>
                  <FinStrip arr={engagedEntries} />
                </Box>
                <Box bg="teal.50" p={2} borderRadius="md" cursor="pointer"
                  onClick={() => setStatusFilter(statusFilter === 'converted' ? 'all' : 'converted')} border={statusFilter === 'converted' ? '2px solid' : 'none'} borderColor="teal.400">
                  <Text fontSize="20px" fontWeight="800" color="teal.600" textAlign="center">{convertedCount}</Text>
                  <Text fontSize="10px" color="gray.500" textAlign="center">Converted</Text>
                  <FinStrip arr={convertedEntries} />
                </Box>
                <Box bg="red.50" p={2} borderRadius="md" cursor="pointer"
                  onClick={() => setStatusFilter(statusFilter === 'rejection' ? 'all' : 'rejection')} border={statusFilter === 'rejection' ? '2px solid' : 'none'} borderColor="red.400">
                  <Text fontSize="20px" fontWeight="800" color="red.600" textAlign="center">{rejectedCount}</Text>
                  <Text fontSize="10px" color="gray.500" textAlign="center">Rejected</Text>
                  <FinStrip arr={rejectedEntries} accentIn="green.600" accentOut="red.500" />
                </Box>
                <Box bg="gray.50" p={2} borderRadius="md" textAlign="center">
                  <Text fontSize="20px" fontWeight="800" color="gray.600">{totalProspects > 0 ? Math.round((engagedCount / totalProspects) * 100) : 0}%</Text>
                  <Text fontSize="10px" color="gray.500">Engagement</Text>
                </Box>
              </SimpleGrid>
            );
          })()}

          {/* Import */}
          {campaignTab === 'prospects' && showImport && (
            <Box bg="teal.50" p={3} borderRadius="md" mb={3} border="1px" borderColor="teal.200">
              <Text fontWeight="600" color="teal.700" mb={1}>Import from Spreadsheet</Text>
              <Text color="gray.500" mb={2}>Paste tab-separated data or upload CSV. Auto-detects columns.</Text>
              <HStack spacing={2} mb={2}>
                <Button size="xs" leftIcon={<FiUpload />} colorScheme="teal" variant="outline" onClick={() => fileInputRef.current?.click()}>Upload CSV</Button>
                <input type="file" ref={fileInputRef} style={{ display:'none' }} accept=".csv,.tsv,.txt" onChange={handleFileUpload} />
                <Button size="xs" leftIcon={<FiClipboard />} colorScheme="teal" variant="outline" onClick={handleParsePreview} isDisabled={!importText.trim()}>Parse</Button>
                {importPreview.length > 0 && <Button size="xs" bg="teal.600" color="white" _hover={{ bg:'teal.700' }} onClick={handleImport} isLoading={importing}>Import {importPreview.length}</Button>}
              </HStack>
              <Textarea size="sm" rows={4} value={importText} fontFamily="mono" placeholder="Paste spreadsheet data here..."
                onChange={(e) => { setImportText(e.target.value); setImportPreview([]); }} mb={1} />
              {importPreview.length > 0 && (
                <Box maxH="200px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" bg="white">
                  <Table size="sm"><Thead position="sticky" top={0} bg="gray.50"><Tr>
                    <Th px={2}>#</Th><Th px={2}>Company</Th><Th px={2}>Contact</Th><Th px={2}>Email</Th><Th px={2}>State</Th><Th px={2}>Info</Th>
                  </Tr></Thead><Tbody>
                    {importPreview.map((row, i) => (
                      <Tr key={i}><Td px={2} color="gray.400">{i+1}</Td><Td px={2} fontWeight="600" maxW="150px" isTruncated>{row.company}</Td>
                        <Td px={2} maxW="120px" isTruncated>{row.contact_name}</Td><Td px={2} maxW="140px" isTruncated color="blue.500">{row.contact_email}</Td>
                        <Td px={2}><Badge colorScheme={row.state?.toUpperCase().includes('INTERESTED') ? 'green' : 'gray'}>{row.state || '—'}</Badge></Td>
                        <Td px={2} maxW="150px" isTruncated color="gray.500">{row.what_they_do || row.requirement}</Td></Tr>
                    ))}
                  </Tbody></Table>
                </Box>
              )}
            </Box>
          )}

          {/* Add existing project */}
          {campaignTab === 'prospects' && showAddExisting && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={3} border="1px" borderColor="blue.200">
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="700" color="blue.700">Add Existing Project to Campaign</Text>
                <HStack spacing={1}>
                  <Button size="xs" variant={existingMode==='company'?'solid':'outline'} colorScheme="blue"
                    onClick={() => { setExistingMode('company'); resetExistingSearch(); }}>
                    🏢 Search by Company
                  </Button>
                  <Button size="xs" variant={existingMode==='project'?'solid':'outline'} colorScheme="blue"
                    onClick={() => { setExistingMode('project'); resetExistingSearch(); }}>
                    📁 Search by Project Name
                  </Button>
                </HStack>
              </Flex>

              {/* COMPANY MODE */}
              {existingMode === 'company' && (
                <Box>
                  {!selectedExistingCompany ? (
                    <Box position="relative">
                      <Input size="sm" placeholder="Type company name..." value={existingSearch}
                        onChange={e => { setExistingSearch(e.target.value); searchCompaniesForExisting(e.target.value); }} />
                      {companySuggestions.length > 0 && (
                        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px"
                          borderColor="blue.200" borderRadius="md" shadow="md" zIndex={20} maxH="160px" overflowY="auto">
                          {companySuggestions.map(co => (
                            <Box key={co.id} px={3} py={2} cursor="pointer" fontSize="12px" _hover={{bg:'blue.50'}}
                              onClick={() => selectExistingCompany(co)}>
                              <Text fontWeight="600">{co.name}</Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <HStack mb={2}>
                        <Badge colorScheme="blue" fontSize="11px" px={2} py={1}>🏢 {selectedExistingCompany.name}</Badge>
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={resetExistingSearch}>Change company</Button>
                      </HStack>
                      {companyProjects.length === 0 ? (
                        <Text fontSize="12px" color="gray.400" py={2}>No eligible projects found for this company.</Text>
                      ) : !selectedExistingProject ? (
                        <Box>
                          <Text fontSize="11px" color="gray.500" mb={2}>Select a project:</Text>
                          <VStack spacing={1} align="stretch" maxH="220px" overflowY="auto">
                            {companyProjects.map(p => (
                              <Box key={p.id} px={3} py={2} bg="white" border="1px" borderColor="blue.100"
                                borderRadius="md" cursor="pointer" _hover={{borderColor:'blue.400',bg:'blue.50'}}
                                onClick={() => selectExistingProject(p)}>
                                <Text fontWeight="600" fontSize="13px">{p.project_name}</Text>
                                {p.row_group_name && <Badge fontSize="8px" colorScheme="gray" mt={0.5}>{p.row_group_name}</Badge>}
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      ) : (
                        <Box>
                          <HStack mb={2}>
                            <Badge colorScheme="purple" fontSize="11px" px={2} py={1}>📁 {selectedExistingProject.project_name}</Badge>
                            <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => { setSelectedExistingProject(null); setProjectTasks([]); }}>Change</Button>
                          </HStack>
                          {projectTasks.length > 0 && (
                            <Box mb={2}>
                              <Text fontSize="11px" color="gray.500" mb={1}>Select a specific task (or add the whole project):</Text>
                              <VStack spacing={1} align="stretch" maxH="160px" overflowY="auto">
                                {projectTasks.map(t => (
                                  <Flex key={t.id} justify="space-between" align="center" px={3} py={2} bg="white"
                                    border="1px" borderColor="gray.200" borderRadius="md">
                                    <Text fontSize="12px">{t.task_title}</Text>
                                    <Button size="xs" colorScheme="blue" isLoading={addingExisting}
                                      onClick={() => handleAddExistingProject(selectedExistingProject, t)}>Add Task</Button>
                                  </Flex>
                                ))}
                              </VStack>
                            </Box>
                          )}
                          <Button size="sm" colorScheme="blue" isLoading={addingExisting}
                            onClick={() => handleAddExistingProject(selectedExistingProject, null)}>
                            Add Whole Project to Campaign
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* PROJECT NAME MODE */}
              {existingMode === 'project' && (
                <Box>
                  <Box mb={2}>
                    <Input size="sm" placeholder="Type project name to search live..."
                      value={existingSearch}
                      onChange={e => { setExistingSearch(e.target.value); searchProjectsByName(e.target.value); }}
                      autoFocus />
                    {existingSearch.length > 0 && existingSearch.length < 2 && <Text fontSize="10px" color="gray.400" mt={1}>Keep typing...</Text>}
                  </Box>
                  {projectNameResults.length > 0 && !selectedExistingProject && (
                    <VStack spacing={1} align="stretch" maxH="220px" overflowY="auto">
                      {projectNameResults.map(p => (
                        <Box key={p.id} px={3} py={2} bg="white" border="1px" borderColor="blue.100"
                          borderRadius="md" cursor="pointer" _hover={{borderColor:'blue.400',bg:'blue.50'}}
                          onClick={() => selectExistingProject(p)}>
                          <Text fontWeight="600" fontSize="13px">{p.project_name}</Text>
                          <HStack spacing={2} mt={0.5}>
                            {p.company_name && <Text fontSize="11px" color="gray.400">{p.company_name}</Text>}
                            {p.row_group_name && <Badge fontSize="8px" colorScheme="gray">{p.row_group_name}</Badge>}
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                  {selectedExistingProject && (
                    <Box>
                      <HStack mb={2}>
                        <Badge colorScheme="purple" fontSize="11px" px={2} py={1}>📁 {selectedExistingProject.project_name}</Badge>
                        <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => { setSelectedExistingProject(null); setProjectTasks([]); }}>Change</Button>
                      </HStack>
                      {projectTasks.length > 0 && (
                        <Box mb={2}>
                          <Text fontSize="11px" color="gray.500" mb={1}>Select a specific task (or add the whole project):</Text>
                          <VStack spacing={1} align="stretch" maxH="160px" overflowY="auto">
                            {projectTasks.map(t => (
                              <Flex key={t.id} justify="space-between" align="center" px={3} py={2} bg="white"
                                border="1px" borderColor="gray.200" borderRadius="md">
                                <Text fontSize="12px">{t.task_title}</Text>
                                <Button size="xs" colorScheme="blue" isLoading={addingExisting}
                                  onClick={() => handleAddExistingProject(selectedExistingProject, t)}>Add Task</Button>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                      )}
                      <Button size="sm" colorScheme="blue" isLoading={addingExisting}
                        onClick={() => handleAddExistingProject(selectedExistingProject, null)}>
                        Add Whole Project to Campaign
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Add prospect */}


          {/* ═══════ KIOSK FORMS PANEL ═══════ */}
          {campaignTab === 'webforms' && (
            <WebFormsModal
              campaignId={selectedCampaign?.id}
              campaignName={selectedCampaign?.name}
            />
          )}
          {campaignTab === 'kiosk' && (
            <Box>
              {kioskLoading ? (
                <Text color="gray.400" textAlign="center" py={8}>Loading kiosk forms…</Text>
              ) : kioskForms.length === 0 ? (
                <Box textAlign="center" py={12} px={4}>
                  <Box fontSize="48px" mb={3}>📋</Box>
                  <Text fontWeight="700" fontSize="lg" color="gray.600" mb={1}>No kiosk forms yet</Text>
                  <Text fontSize="sm" color="gray.400" mb={5}>
                    Create a kiosk form to capture leads at trade shows and events.<br />
                    Each form gets a unique URL and QR code.
                  </Text>
                  <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={() => { setKioskEditForm(null); setKioskBuilderOpen(true); }}>
                    Create First Form
                  </Button>
                </Box>
              ) : (
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                  {kioskForms.map(kf => {
                    const kioskUrl = `${window.location.origin}/prospect-kiosk/${kf.token}`;
                    return (
                      <Box key={kf.id} bg="white" border="1px" borderColor="gray.200" borderRadius="xl"
                        boxShadow="sm" overflow="hidden" _hover={{ boxShadow: 'md' }} transition="box-shadow 0.15s">
                        {/* Colour stripe */}
                        <Box h="5px" bg={kf.branding_color || '#EE7203'} />
                        <Box p={4}>
                          <HStack justify="space-between" align="flex-start" mb={2}>
                            <Box flex={1}>
                              <Text fontWeight="800" fontSize="sm" noOfLines={2}>{kf.title}</Text>
                              {kf.description && <Text fontSize="xs" color="gray.500" noOfLines={2} mt={0.5}>{kf.description}</Text>}
                            </Box>
                            {kf.password_protected && (
                              <Tooltip label="Password protected"><Box color="orange.400" fontSize="14px">🔒</Box></Tooltip>
                            )}
                          </HStack>

                          <HStack spacing={2} mb={3} flexWrap="wrap">
                            <Badge colorScheme="purple" variant="subtle" fontSize="10px">
                              {kf.submission_count || 0} submissions
                            </Badge>
                            {kf.qr_enabled && <Badge colorScheme="blue" variant="subtle" fontSize="10px">QR on</Badge>}
                            {(kf.custom_fields || []).length > 0 && (
                              <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                                {kf.custom_fields.length} custom field{kf.custom_fields.length !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </HStack>

                          <Box bg="gray.50" borderRadius="md" p={2} mb={3}>
                            <Text fontSize="10px" color="gray.400" fontFamily="mono" noOfLines={1}
                              title={kioskUrl}>/prospect-kiosk/{kf.token.slice(0, 16)}…</Text>
                          </Box>

                          <HStack spacing={1} justify="flex-end">
                            <Tooltip label="Copy URL">
                              <IconButton icon={<FiLink />} size="xs" variant="ghost"
                                onClick={() => navigator.clipboard.writeText(kioskUrl).then(() => toast({ title: 'URL copied', status: 'success', duration: 1500 }))}
                                aria-label="Copy URL" />
                            </Tooltip>
                            <Tooltip label="Open kiosk">
                              <IconButton icon={<FiEye />} size="xs" variant="ghost"
                                onClick={() => window.open(kioskUrl, '_blank')}
                                aria-label="Open" />
                            </Tooltip>
                            <Tooltip label="Duplicate">
                              <IconButton icon={<FiCopy />} size="xs" variant="ghost"
                                onClick={() => { setKioskEditForm(kf); setKioskBuilderOpen(true); setTimeout(() => {}, 100); }}
                                aria-label="Duplicate" />
                            </Tooltip>
                            <Button size="xs" leftIcon={<FiEdit2 />} colorScheme="orange" variant="outline"
                              onClick={() => { setKioskEditForm(kf); setKioskBuilderOpen(true); }}>
                              Edit
                            </Button>
                            <Tooltip label="Delete form">
                              <IconButton icon={<FiTrash2 />} size="xs" colorScheme="red" variant="ghost"
                                onClick={() => setKioskDeleteId(kf.token)} aria-label="Delete" />
                            </Tooltip>
                          </HStack>
                        </Box>
                      </Box>
                    );
                  })}
                  {/* Add new card */}
                  <Box border="2px dashed" borderColor="orange.200" borderRadius="xl" p={6}
                    display="flex" alignItems="center" justifyContent="center" flexDirection="column"
                    cursor="pointer" _hover={{ bg: 'orange.50', borderColor: 'orange.400' }}
                    transition="all 0.15s" onClick={() => { setKioskEditForm(null); setKioskBuilderOpen(true); }}>
                    <Box fontSize="28px" mb={2}>➕</Box>
                    <Text fontSize="sm" color="orange.500" fontWeight="600">New Form</Text>
                  </Box>
                </SimpleGrid>
              )}
            </Box>
          )}

          {/* Status filter */}
          {campaignTab === 'prospects' && <HStack spacing={1} mb={2} flexWrap="wrap">
            <Badge cursor="pointer" colorScheme={statusFilter === 'all' ? 'purple' : 'gray'} variant={statusFilter === 'all' ? 'solid' : 'outline'}
              onClick={() => setStatusFilter('all')}>All ({entries.length})</Badge>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const count = entries.filter(e => e.prospect_status === key).length;
              if (count === 0) return null;
              return (<Badge key={key} cursor="pointer" colorScheme={statusFilter === key ? cfg.color : 'gray'}
                variant={statusFilter === key ? 'solid' : 'outline'}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}>{cfg.label} ({count})</Badge>);
            })}
          </HStack>}

          {/* ═══════ PROSPECT CARDS ═══════ */}
          {campaignTab === 'prospects' && STATUS_GROUPS.map(group => {
            const ge = filteredEntries.filter(e => group.statuses.includes(e.prospect_status));
            if (ge.length === 0) return null;
            return (
              <Box key={group.title} mb={4}>
                <Box bg="gray.700" color="white" px={2} py={0.5} borderRadius="sm" mb={2} display="inline-block">
                  <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">{group.title} ({ge.length})</Text>
                </Box>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={3}>
                  {ge.map(entry => {
                    const cfg = STATUS_CONFIG[entry.prospect_status] || STATUS_CONFIG.new;
                    const oppVal = parseFloat(entry.opportunity_value || 0);
                    return (
                      <Box key={entry.id} bg="white" border="1px" borderColor="gray.300" borderRadius="lg"
                        p={3} boxShadow="sm" _hover={{ boxShadow: 'md', borderColor: cfg.color || 'blue.400' }}
                        borderTop="3px solid" borderTopColor={cfg.color || 'gray.400'} transition="all 0.15s">
                        {/* Header: company + logo */}
                        <Flex justify="space-between" align="start" mb={2}>
                          <HStack spacing={2} flex={1} minW={0} pr={2}>
                            {entry.company_logo_url ? (
                              <Image src={`/api/files/${entry.company_logo_url}`} maxH="24px" maxW="36px" objectFit="contain" fallback={null} />
                            ) : null}
                            <Box minW={0}>
                              <Text fontSize="12px" fontWeight="700" lineHeight="1.2" noOfLines={1}>{entry.company_name || '(no company)'}</Text>
                              {entry.first_name && <Text fontSize="9px" color="gray.500">{entry.first_name} {entry.last_name || ''}</Text>}
                            </Box>
                          </HStack>
                          {/* Top-right: status badge + financials */}
                          <Box textAlign="right" flexShrink={0}>
                            <Badge colorScheme={cfg.colorScheme || 'gray'} fontSize="8px" display="block" mb={1}>{cfg.label}</Badge>
                            {(() => {
                              const cardIn  = parseFloat(entry.inv_in_total  || 0);
                              const cardOut = parseFloat(entry.inv_out_total || 0);
                              const cardBal = cardIn - cardOut;
                              if (cardIn === 0 && cardOut === 0) return null;
                              return (
                                <Box>
                                  {cardIn  > 0 && <Text fontSize="9px" fontWeight="700" color="green.600" lineHeight="1.3">IN £{cardIn.toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0})}</Text>}
                                  {cardOut > 0 && <Text fontSize="9px" fontWeight="700" color="red.500"   lineHeight="1.3">OUT £{cardOut.toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0})}</Text>}
                                  <Text fontSize="9px" fontWeight="800" color={cardBal >= 0 ? 'green.700' : 'red.600'} lineHeight="1.4" borderTop="1px" borderColor="gray.200" mt={0.5} pt={0.5}>
                                    {cardBal >= 0 ? '' : '-'}£{Math.abs(cardBal).toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0})}
                                  </Text>
                                </Box>
                              );
                            })()}
                          </Box>
                        </Flex>

                        {/* Opportunity value */}
                        {oppVal > 0 && <Text fontSize="15px" fontWeight="800" color="green.600" mb={1}>£{oppVal.toLocaleString('en-GB')}</Text>}

                        {/* Contact info */}
                        <VStack spacing={0.5} align="start" mb={2} fontSize="10px" color="gray.500">
                          {entry.contact_email && <Text color="blue.500">{entry.contact_email}</Text>}
                          {(entry.contact_phone || entry.company_phone) && <Text>{entry.contact_phone || entry.company_phone}</Text>}
                        </VStack>

                        {/* Last Out / Last In */}
                        <HStack spacing={3} mb={2} fontSize="10px">
                          <Box><Text color="gray.400" fontWeight="600">Last Out</Text>
                            <Text color="gray.600">{fmtDate(entry.last_outbound_date || entry.last_outreach_date) || '—'}</Text></Box>
                          <Box><Text color="gray.400" fontWeight="600">Last In</Text>
                            <Text color={entry.last_inbound_date || entry.last_response_date ? 'green.600' : 'gray.400'}>
                              {fmtDate(entry.last_inbound_date || entry.last_response_date) || '—'}</Text></Box>
                        </HStack>

                        {/* Status dropdown */}
                        <Select size="xs" fontSize="10px" mb={2} value={entry.prospect_status}
                          onChange={(e) => handleStatusChange(entry.id, e.target.value)}>
                          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </Select>

                        {/* Conversion likelihood */}
                        {(() => {
                          const dispVal = pendingConv[entry.id] !== undefined ? pendingConv[entry.id] : (entry.conversion_likelihood || 0);
                          const scheme = dispVal >= 70 ? 'green' : dispVal >= 40 ? 'orange' : 'gray';
                          const textColor = dispVal >= 70 ? 'green.600' : dispVal >= 40 ? 'orange.500' : 'gray.500';
                          return (
                            <>
                              <Flex justify="space-between" align="center" mb={1}>
                                <Text fontSize="9px" color="gray.400" fontWeight="600">CONVERSION</Text>
                                <Text fontSize="10px" fontWeight="700" color={textColor}>{dispVal}%</Text>
                              </Flex>
                              <Slider size="sm" min={0} max={100} step={5} mb={2}
                                value={dispVal}
                                onChange={(v) => handleConvLiveChange(entry.id, v)}
                                onChangeEnd={(v) => handleConvChangeEnd(entry, v)}
                                colorScheme={scheme}>
                                <SliderTrack h="4px"><SliderFilledTrack /></SliderTrack>
                                <SliderThumb boxSize={3} _focus={{ boxShadow: 'none' }} />
                              </Slider>
                            </>
                          );
                        })()}

                        {/* Notes / rejection */}
                        {entry.rejection_reason && <Text fontSize="9px" color="red.500" mb={1}>Rejected: {entry.rejection_reason}</Text>}
                        {entry.notes && !entry.rejection_reason && <Text fontSize="9px" color="gray.400" noOfLines={2} mb={1}>{entry.notes}</Text>}

                        {/* Action buttons */}
                        <Divider mb={1.5} />
                        <HStack spacing={0} justify="center" flexWrap="wrap">
                          {/* Edit prospect details */}
                          <Tooltip label="Edit Prospect Details">
                            <IconButton icon={<FiEdit2 size={14} />} size="xs" variant="ghost" colorScheme="gray"
                              onClick={() => {
                                setEditTaskEntry(entry);
                                taskEditDisclosure.onOpen();
                              }} aria-label="Edit Prospect" />
                          </Tooltip>
                          {/* Conversion & Status History */}
                          <Tooltip label="View Status & Conversion History">
                            <IconButton icon={<FiActivity size={14} />} size="xs" variant="ghost" colorScheme="blue"
                              onClick={() => { setHistoryEntry(entry); historyDisclosure.onOpen(); }}
                              aria-label="History" />
                          </Tooltip>
                          {/* Sub-tasks / To Do */}
                          {entry.task_id && (
                            <Tooltip label="Sub-Tasks / To Do">
                              <IconButton icon={<FiCheckSquare size={14} />} size="xs" variant="ghost" colorScheme="teal"
                                onClick={() => openSubTasks(entry)} aria-label="Sub Tasks" />
                            </Tooltip>
                          )}
                          {/* Clock On */}
                          {entry.task_id && (
                            <Tooltip label="Clock On / Work Session">
                              <IconButton icon={<FiPlay size={14} />} size="xs" variant="ghost" colorScheme="green"
                                onClick={() => {
                                  setWorkSessionEntry(entry);
                                  workSessionDisclosure.onOpen();
                                }} aria-label="Clock On" />
                            </Tooltip>
                          )}
                          {entry.prospect_status === 'rejection' ? (
                            <Tooltip label="Restore (Unreject)"><IconButton icon={<FiRefreshCw size={16} />} size="xs" variant="ghost" colorScheme="green"
                              onClick={() => setUnrejectEntry(entry)} aria-label="Unreject" /></Tooltip>
                          ) : (
                            <>
                            <Tooltip label="Mark Engaged"><IconButton icon={<FiCheck size={16} />} size="xs" variant="ghost" colorScheme="green"
                              onClick={() => handleResponse(entry.id, false)} aria-label="Engaged" /></Tooltip>
                            <Tooltip label="Mark Rejected"><IconButton icon={<FiX size={16} />} size="xs" variant="ghost" colorScheme="red"
                              onClick={() => setRejectEntry(entry)} aria-label="Reject" /></Tooltip>
                            {(entry.lead_source_method === 'kiosk_form' || entry.lead_source_method === 'web_form') && (
                              <Tooltip label={`Mark as Spam — deletes ${entry.company_name || 'company'} & contact`}>
                                <IconButton icon={<FiSlash size={14} />} size="xs" variant="ghost" colorScheme="orange"
                                  onClick={() => setSpamEntry(entry)} aria-label="Mark Spam" />
                              </Tooltip>
                            )}
                            </>
                          )}
                          {entry.project_id && (
                            <>
                            <Box display="inline-block" position="relative">
                              <Tooltip label="Client Quotes and Invoices (£ In)"><IconButton
                                icon={<Text fontSize="11px" fontWeight="800">£</Text>}
                                size="xs" variant={entry.inv_in_count > 0 ? 'solid' : 'ghost'} borderRadius="sm"
                                bg={entry.inv_in_count > 0 ? 'green.500' : undefined}
                                color={entry.inv_in_count > 0 ? 'white' : 'gray.300'}
                                _hover={{ bg: 'green.500', color: 'white' }}
                                onClick={() => openInvoiceIn(entry.project_id)} aria-label="Client Quotes and Invoices" /></Tooltip>
                              {entry.inv_in_count > 0 && <Text fontSize="7px" fontWeight="700" color="green.700" position="absolute" top="-3px" right="-5px">{entry.inv_in_count}</Text>}
                            </Box>
                            <Box display="inline-block" position="relative">
                              <Tooltip label="Costs — Quotes and Invoices (£ Out)"><IconButton
                                icon={<Text fontSize="11px" fontWeight="800">£</Text>}
                                size="xs" variant={entry.inv_out_count > 0 ? 'solid' : 'ghost'} borderRadius="sm"
                                bg={entry.inv_out_count > 0 ? 'red.500' : undefined}
                                color={entry.inv_out_count > 0 ? 'white' : 'gray.300'}
                                _hover={{ bg: 'red.500', color: 'white' }}
                                onClick={() => openInvoiceOut(entry.project_id)} aria-label="Costs Invoices" /></Tooltip>
                              {entry.inv_out_count > 0 && <Text fontSize="7px" fontWeight="700" color="red.600" position="absolute" top="-3px" right="-5px">{entry.inv_out_count}</Text>}
                            </Box>
                            <Tooltip label="Request an Appointment">
                              <IconButton icon={<FiCalendar size={14} />} size="xs" variant="ghost" colorScheme="teal"
                                onClick={() => openDiary(entry)} aria-label="Request Appointment" />
                            </Tooltip>
                            <Box display="inline-block" position="relative">
                              <Tooltip label="Outreach"><IconButton icon={<FiMail size={16} />} size="xs" variant="ghost" colorScheme="blue"
                                onClick={() => openComms(entry.project_id, entry.task_id)} aria-label="Outreach" /></Tooltip>
                              {entry.comm_count > 0 && <Text fontSize="7px" fontWeight="700" color="blue.600" position="absolute" top="-3px" right="-5px">{entry.comm_count}</Text>}
                            </Box>
                            <Box display="inline-block" position="relative">
                              <Tooltip label="Notes"><IconButton icon={<FiFileText size={16} />} size="xs" variant="ghost" colorScheme="purple"
                                onClick={() => openNotes(entry.project_id)} aria-label="Notes" /></Tooltip>
                              {entry.notes_count > 0 && <Text fontSize="7px" fontWeight="700" color="purple.600" position="absolute" top="-3px" right="-5px">{entry.notes_count}</Text>}
                            </Box>
                            </>
                          )}
                        </HStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>
            );
          })}

          {campaignTab === 'prospects' && filteredEntries.length === 0 && (
            <Text color="gray.500" textAlign="center" py={8}>{entries.length === 0 ? 'No prospects yet.' : 'No matches.'}</Text>
          )}
    </PageShell>

    {/* Exact same Comms + Notes modals used by MainGrid */}
    {modalProjectId && (
      <>
        <CommunicationsModal projectId={modalProjectId} taskId={modalTaskId} isOpen={commDisclosure.isOpen}
          onClose={() => { commDisclosure.onClose(); if (selectedCampaign) fetchEntries(selectedCampaign.id); }} />
        <NotesModal projectId={modalProjectId} isOpen={notesDisclosure.isOpen}
          onClose={() => { notesDisclosure.onClose(); if (selectedCampaign) fetchEntries(selectedCampaign.id); }} />
        <InvoiceModal projectId={modalProjectId} direction="out" isOpen={invoiceInDisclosure.isOpen}
          onClose={() => { invoiceInDisclosure.onClose(); if (selectedCampaign) fetchEntries(selectedCampaign.id); }} />
        <InvoiceModal projectId={modalProjectId} direction="in" isOpen={invoiceOutDisclosure.isOpen}
          onClose={() => { invoiceOutDisclosure.onClose(); if (selectedCampaign) fetchEntries(selectedCampaign.id); }} />
        <InvoiceModal projectId={modalProjectId} direction="out" initialStatus="quotation_not_sent" isOpen={invoiceQuoteDisclosure.isOpen}
          onClose={() => { invoiceQuoteDisclosure.onClose(); if (selectedCampaign) fetchEntries(selectedCampaign.id); }} />
      </>
    )}
    {/* Request an Appointment modal */}
    <AppointmentModal isOpen={diaryDisclosure.isOpen} onClose={diaryDisclosure.onClose}
      onSaved={() => {}} prefill={diaryPrefill?._editAppt ? null : diaryPrefill}
      editAppt={diaryPrefill?._editAppt || null} />

    {/* Contact picker — multi-select with company contacts + search all */}
    <Modal isOpen={contactPickerOpen} onClose={() => setContactPickerOpen(false)} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={1}>
          Book Appointment
          {contactPickerEntry?.company_name && <Text fontSize="11px" fontWeight="400" color="gray.500" mt={0.5}>{contactPickerEntry.company_name}</Text>}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          {/* Search all contacts — at the top */}
          <Box mb={3}>
            <HStack>
              <Input size="sm" placeholder="Search all contacts by name or email..." value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter' && pickerSearch.trim()) {
                    try { const { data } = await api.get(`/contacts?search=${encodeURIComponent(pickerSearch)}&limit=20`); setPickerResults(data || []); } catch(err) {}
                  }
                }} />
              <Button size="sm" colorScheme="gray" onClick={async () => {
                if (!pickerSearch.trim()) return;
                try { const { data } = await api.get(`/contacts?search=${encodeURIComponent(pickerSearch)}&limit=20`); setPickerResults(data || []); } catch(err) {}
              }}>Search</Button>
            </HStack>
            {pickerResults.length > 0 && (
              <Box mt={2} border="1px" borderColor="gray.200" borderRadius="md" maxH="140px" overflowY="auto">
                {pickerResults.map(c => {
                  const sel = pickerSelected.some(x => x.id === c.id);
                  return (
                    <Flex key={c.id} px={3} py={2} cursor="pointer" align="center" justify="space-between"
                      bg={sel ? 'teal.50' : 'white'} _hover={{ bg: 'gray.50' }}
                      onClick={() => setPickerSelected(prev => sel ? prev.filter(x => x.id !== c.id) : [...prev, c])}>
                      <Box>
                        <Text fontSize="12px" fontWeight="600">{c.first_name} {c.last_name || ''}</Text>
                        {c.email && <Text fontSize="10px" color="gray.400">{c.email}</Text>}
                      </Box>
                      {sel && <Text fontSize="11px" color="teal.500" fontWeight="700">✓</Text>}
                    </Flex>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Company contacts as toggle pills */}
          {contactPickerContacts.length > 0 && (
            <Box mb={3}>
              <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>Company Contacts</Text>
              <Flex gap={2} flexWrap="wrap">
                {contactPickerContacts.map(c => {
                  const sel = pickerSelected.some(x => x.id === c.id);
                  return (
                    <Box key={c.id} as="button"
                      onClick={() => setPickerSelected(prev => sel ? prev.filter(x => x.id !== c.id) : [...prev, c])}
                      px={3} py={1.5} borderRadius="full" fontSize="12px" fontWeight="600" cursor="pointer"
                      border="2px solid" transition="all 0.1s"
                      borderColor={sel ? 'teal.400' : 'gray.200'}
                      bg={sel ? 'teal.50' : 'white'}
                      color={sel ? 'teal.700' : 'gray.700'}
                      _hover={{ borderColor: 'teal.300', bg: 'teal.50' }}>
                      {c.first_name} {c.last_name || ''}{sel && <span style={{marginLeft:'5px'}}>✓</span>}
                    </Box>
                  );
                })}
              </Flex>
            </Box>
          )}
          {contactPickerContacts.length === 0 && pickerResults.length === 0 && (
            <Text fontSize="12px" color="gray.400" mb={3}>No company contacts found. Use search to find contacts.</Text>
          )}

          {/* Selected summary */}
          {pickerSelected.length > 0 && (
            <Box mb={3} p={2} bg="teal.50" borderRadius="md" border="1px" borderColor="teal.200">
              <Text fontSize="10px" color="teal.600" fontWeight="700" mb={1}>INVITING:</Text>
              <Text fontSize="12px" color="teal.800">{pickerSelected.map(c => `${c.first_name} ${c.last_name||''}`).join(', ')}</Text>
            </Box>
          )}

          <Flex gap={2} justify="flex-end">
            <Button size="sm" variant="ghost" onClick={() => setContactPickerOpen(false)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" isDisabled={pickerSelected.length === 0}
              onClick={() => {
                setContactPickerOpen(false);
                setDiaryPrefill({
                  contact_ids: pickerSelected.map(c => c.id),
                  prefill_contacts: pickerSelected,
                  company_id: contactPickerEntry?.company_id || null,
                  project_id: contactPickerEntry?.project_id || ''
                });
                diaryDisclosure.onOpen();
              }}>
              Request Appointment{pickerSelected.length > 0 ? ` (${pickerSelected.length})` : ''}
            </Button>
          </Flex>

          {/* Existing bookings for this company/project */}
          <Box mt={4} pt={3} borderTop="1px" borderColor="gray.100">
            <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
              Existing Appointment Requests
            </Text>
            {pickerBookingsLoading ? (
              <Text fontSize="11px" color="gray.400">Loading...</Text>
            ) : pickerBookings.length === 0 ? (
              <Text fontSize="11px" color="gray.400" fontStyle="italic">No appointment requests found.</Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {pickerBookings.map(b => {
                  const statusColors = { draft:'gray', sent:'blue', confirmed:'green', rescheduled:'orange', cancelled:'red' };
                  const statusColor = statusColors[b.status] || 'gray';
                  const bookingUrl = `${window.location.origin}/book/${b.token}`;
                  return (
                    <Box key={b.id} p={2} border="1px" borderColor="gray.200" borderRadius="md" bg="white">
                      <Flex justify="space-between" align="flex-start" mb={1}>
                        <Box flex={1} minW={0} pr={2}>
                          <Text fontSize="12px" fontWeight="700" noOfLines={1}>{b.title || 'Appointment Request'}</Text>
                          {b.project_name && <Text fontSize="10px" color="gray.500" noOfLines={1}>{b.project_name}</Text>}
                        </Box>
                        <Badge colorScheme={statusColor} fontSize="9px" flexShrink={0} textTransform="capitalize">{b.status}</Badge>
                      </Flex>
                      <Flex gap={1} align="center" mt={1} flexWrap="wrap">
                        <Text fontSize="10px" color="gray.400">
                          {b.appointment_type?.replace(/_/g,' ')}
                          {b.contact_count > 0 && ` · ${b.contact_count} contact${b.contact_count > 1 ? 's' : ''}`}
                          {b.confirmed_date && ` · ${new Date(b.confirmed_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}`}
                        </Text>
                      </Flex>
                      <HStack spacing={1} mt={2}>
                        <Tooltip label="Copy booking link">
                          <Button size="xs" variant="outline" colorScheme="blue" leftIcon={<span style={{fontSize:'10px'}}>🔗</span>}
                            onClick={() => { navigator.clipboard.writeText(bookingUrl); }}>
                            Copy Link
                          </Button>
                        </Tooltip>
                        <Tooltip label="Open booking link in new tab">
                          <Button size="xs" variant="ghost" colorScheme="blue"
                            onClick={() => window.open(bookingUrl, '_blank')}>
                            Open
                          </Button>
                        </Tooltip>
                        <Button size="xs" variant="ghost" colorScheme="purple"
                          onClick={() => {
                            setContactPickerOpen(false);
                            // Re-open diary modal in edit mode by triggering AppointmentModal with this appt
                            setDiaryPrefill(null);
                            // We'll use a separate edit disclosure — store the appt id
                            setPickerBookings([]); // clear so it refetches
                            api.get(`/appointments/${b.id}`).then(r => {
                              setDiaryPrefill({ _editAppt: r.data });
                              diaryDisclosure.onOpen();
                            }).catch(()=>{});
                          }}>
                          Edit
                        </Button>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={!!rejectEntry} onClose={() => setRejectEntry(null)}
      title="Mark as Rejected" message={`Reject "${rejectEntry?.company_name || ''}"?`}
      showInput inputLabel="Rejection reason (optional)" inputPlaceholder="e.g. Not interested, budget constraints..."
      confirmLabel="Reject" confirmColor="red"
      onConfirm={(reason) => { if (rejectEntry) handleResponse(rejectEntry.id, true, reason); setRejectEntry(null); }} />
    <ConfirmModal isOpen={!!unrejectEntry} onClose={() => setUnrejectEntry(null)}
      title="Restore Prospect" message={`Restore "${unrejectEntry?.company_name || ''}" from rejected status? They will be moved back to New.${unrejectEntry?.rejection_reason ? `\n\nOriginal rejection: ${unrejectEntry.rejection_reason}` : ''}`}
      showInput inputLabel="Add a note (optional)" inputPlaceholder="Reason for restoring..."
      confirmLabel="Restore" confirmColor="green"
      onConfirm={(note) => { if (unrejectEntry) handleUnreject(unrejectEntry.id, note); setUnrejectEntry(null); }} />
      {/* Add Prospect Modal — full ProspectsModal with campaign context */}
      <ProspectsModal
        isOpen={addProspectModalDisc.isOpen}
        onClose={addProspectModalDisc.onClose}
        campaignId={selectedCampaign?.id}
        campaignName={selectedCampaign?.name}
        onRefresh={() => selectedCampaign && fetchEntries(selectedCampaign.id)}
      />
      {/* Kiosk Form Builder */}
      {/* ── Spam Confirm Modal ── */}
      {spamEntry && (
        <Box position="fixed" inset={0} zIndex={1500} bg="blackAlpha.600" display="flex" alignItems="center" justifyContent="center" p={4}>
          <Box bg="white" borderRadius="xl" maxW="460px" w="100%" boxShadow="2xl" overflow="hidden">
            <Box bg="orange.500" px={5} py={4}>
              <Text fontSize="15px" fontWeight="700" color="white">🚫 Mark as Spam?</Text>
            </Box>
            <Box px={5} py={4}>
              <Text fontSize="14px" color="gray.700" mb={3}>
                Are you sure you wish to delete the company and contact and mark this lead as spam?
              </Text>
              <Box bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" p={3} mb={4}>
                <Text fontSize="13px" fontWeight="700" color="orange.800">{spamEntry.company_name || '(no company)'}</Text>
                {spamEntry.first_name && <Text fontSize="12px" color="orange.700">{[spamEntry.first_name, spamEntry.last_name].filter(Boolean).join(' ')}</Text>}
                {spamEntry.contact_email && <Text fontSize="11px" color="gray.500">{spamEntry.contact_email}</Text>}
                <Badge mt={1} colorScheme={spamEntry.lead_source_method === 'web_form' ? 'blue' : 'orange'} fontSize="9px">
                  {spamEntry.lead_source_method === 'web_form' ? '🌐 Web Form' : '📋 Kiosk'} lead
                </Badge>
              </Box>
              <Text fontSize="12px" color="red.600" mb={4}>
                ⚠️ This will permanently delete the company, contact, and communications from the main system. The record will be saved in Lead Spam (admin Settings) where it can be restored or permanently deleted.
              </Text>
              <HStack justify="flex-end" spacing={2}>
                <Button size="sm" variant="ghost" onClick={() => setSpamEntry(null)} isDisabled={spamConfirming}>Cancel</Button>
                <Button size="sm" colorScheme="orange" onClick={handleSpam} isLoading={spamConfirming} loadingText="Moving to spam…">
                  Yes, Mark as Spam
                </Button>
              </HStack>
            </Box>
          </Box>
        </Box>
      )}
      {/* ── Conversion Log Modal ── */}
      {convLogEntry && (
        <Box position="fixed" inset={0} zIndex={1500} bg="blackAlpha.600" display="flex" alignItems="center" justifyContent="center" p={4}>
          <Box bg="white" borderRadius="xl" maxW="420px" w="100%" boxShadow="2xl" overflow="hidden">
            <Box bg="orange.500" px={5} py={4}>
              <Text fontSize="14px" fontWeight="700" color="white">📈 Conversion Likelihood Changed</Text>
            </Box>
            <Box px={5} py={4}>
              <HStack spacing={4} mb={3} justify="center">
                <Box textAlign="center">
                  <Text fontSize="10px" color="gray.400" mb={0.5}>WAS</Text>
                  <Text fontSize="22px" fontWeight="800" color="gray.400">{convLogEntry.oldVal}%</Text>
                </Box>
                <Text fontSize="20px" color="orange.400">→</Text>
                <Box textAlign="center">
                  <Text fontSize="10px" color="orange.500" mb={0.5}>NOW</Text>
                  <Text fontSize="22px" fontWeight="800" color={convLogEntry.newVal >= 70 ? 'green.600' : convLogEntry.newVal >= 40 ? 'orange.500' : 'gray.600'}>
                    {convLogEntry.newVal}%
                  </Text>
                </Box>
              </HStack>
              <Text fontSize="12px" color="gray.600" mb={2}>Please log why the conversion likelihood changed:</Text>
              <Textarea
                size="sm" placeholder="e.g. Had a positive call, requested a quote, showed buying intent…"
                value={convLogNote} onChange={e => setConvLogNote(e.target.value)}
                rows={3} mb={4}
                autoFocus
              />
              <HStack justify="flex-end" spacing={2}>
                <Button size="sm" variant="ghost" onClick={cancelConvLog} isDisabled={convLogSaving}>Cancel</Button>
                <Button size="sm" colorScheme="orange" onClick={saveConvLog} isLoading={convLogSaving} loadingText="Saving…">
                  Save Change
                </Button>
              </HStack>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Sub-Tasks / To Do Drawer ── */}
      {subTaskDisclosure.isOpen && subTaskEntry && (
        <Box position="fixed" inset={0} zIndex={1500} bg="blackAlpha.500" display="flex" alignItems="center" justifyContent="center" p={4}>
          <Box bg="white" borderRadius="xl" maxW="460px" w="100%" maxH="80vh" display="flex" flexDir="column" boxShadow="2xl" overflow="hidden">
            <Flex bg="teal.600" px={5} py={3} align="center" justify="space-between">
              <Box>
                <Text fontSize="13px" fontWeight="700" color="white">Sub-Tasks / To Do</Text>
                <Text fontSize="10px" color="teal.100">{subTaskEntry.company_name || subTaskEntry.task_title || 'Prospect'}</Text>
              </Box>
              <Button size="xs" variant="ghost" color="white" _hover={{bg:'teal.700'}} onClick={subTaskDisclosure.onClose}>✕</Button>
            </Flex>
            <Box flex={1} overflowY="auto" px={4} py={3}>
              {subTasksLoading ? (
                <Text fontSize="12px" color="gray.400" textAlign="center" py={4}>Loading…</Text>
              ) : subTasks.length === 0 ? (
                <Text fontSize="12px" color="gray.400" textAlign="center" py={4}>No sub-tasks yet. Add one below.</Text>
              ) : (
                <VStack spacing={1} align="stretch">
                  {subTasks.map(st => (
                    <Flex key={st.id} align="center" gap={2} p={2} borderRadius="md"
                      bg={st.is_complete ? 'green.50' : 'gray.50'} border="1px" borderColor={st.is_complete ? 'green.200' : 'gray.200'}
                      cursor="pointer" onClick={() => toggleSubTask(st)}
                      _hover={{bg: st.is_complete ? 'green.100' : 'gray.100'}}>
                      <Box w="16px" h="16px" borderRadius="4px" border="2px"
                        borderColor={st.is_complete ? 'green.500' : 'gray.400'}
                        bg={st.is_complete ? 'green.500' : 'white'}
                        display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        {!!st.is_complete && <Text fontSize="9px" color="white" fontWeight="900">✓</Text>}
                      </Box>
                      <Text fontSize="12px" color={st.is_complete ? 'gray.400' : 'gray.700'}
                        textDecoration={st.is_complete ? 'line-through' : 'none'}
                        flex={1}>{st.title}</Text>
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>
            <Box px={4} py={3} borderTop="1px" borderColor="gray.100">
              <HStack>
                <Input size="sm" placeholder="Add sub-task…" value={newSubTask}
                  onChange={e => setNewSubTask(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubTask(); } }} />
                <Button size="sm" colorScheme="teal" onClick={addSubTask} isDisabled={!newSubTask.trim()}>Add</Button>
              </HStack>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Work Session Modal ── */}
      {workSessionDisclosure.isOpen && workSessionEntry && workSessionEntry.task_id && (
        <WorkSessionModal
          isOpen={workSessionDisclosure.isOpen}
          onClose={workSessionDisclosure.onClose}
          taskId={workSessionEntry.task_id}
          taskTitle={workSessionEntry.task_title || workSessionEntry.company_name || 'Prospect Task'}
          projectId={workSessionEntry.project_id}
        />
      )}

      {/* ── Prospect Edit Modal ── */}
      {taskEditDisclosure.isOpen && editTaskEntry && (
        <ProspectEditModal
          isOpen={taskEditDisclosure.isOpen}
          onClose={taskEditDisclosure.onClose}
          entry={editTaskEntry}
          onSaved={() => { if (selectedCampaign) fetchEntries(selectedCampaign.id); }}
        />
      )}

      {/* ── History Modal (history tab pre-selected) ── */}
      {historyDisclosure.isOpen && historyEntry && (
        <ProspectEditModal
          isOpen={historyDisclosure.isOpen}
          onClose={historyDisclosure.onClose}
          entry={historyEntry}
          onSaved={() => { if (selectedCampaign) fetchEntries(selectedCampaign.id); }}
          defaultTab={3}
        />
      )}

      {/* Kiosk delete confirm */}
      <ConfirmModal
        isOpen={!!kioskDeleteId}
        onClose={() => setKioskDeleteId(null)}
        title="Delete Kiosk Form"
        message="Are you sure you want to delete this kiosk form? This cannot be undone. Existing submission records will be preserved."
        confirmLabel="Delete"
        confirmColor="red"
        onConfirm={() => handleDeleteKioskForm(kioskDeleteId)}
      />

      {/* Company picker modal — shown when a project has multiple linked companies */}
      {pendingAddPayload && (
        <Modal isOpen={true} onClose={() => setPendingAddPayload(null)} size="sm" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontSize="md" pb={1}>
              Which company is this prospect?
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="12px" color="gray.500" mb={3}>
                <strong>{pendingAddPayload.project.project_name}</strong> has multiple companies linked. Choose which one to associate with this campaign entry:
              </Text>
              <VStack spacing={2} align="stretch">
                {projectLinkedCompanies.map(c => {
                  const cid = c.company_id || c.id;
                  const cname = c.name || c.company_name || '—';
                  return (
                    <Button key={cid} variant="outline" colorScheme="blue" size="sm"
                      justifyContent="flex-start" fontWeight="600"
                      onClick={() => doAddExistingProject(pendingAddPayload.project, pendingAddPayload.task, cid)}>
                      {c.is_primary ? '⭐ ' : ''}{cname}
                    </Button>
                  );
                })}
                <Button variant="ghost" size="sm" colorScheme="gray"
                  onClick={() => doAddExistingProject(pendingAddPayload.project, pendingAddPayload.task, null)}>
                  Add without company
                </Button>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" onClick={() => setPendingAddPayload(null)}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
